/**
 * @fullvsl/contracts/resume-schema — Resume-play persistence contract (FUL-12).
 *
 * Formalizes, as a typed contract extending FUL-5 (§5), the four resume-play
 * persistence items the directive names:
 *  §A  Last timestamp + max watched position — carried on `ResumeState`
 *      (positionSec / maxWatchedSec, defined in contracts.ts). This module owns
 *      the deterministic composite key so a returning visitor is recognized
 *      without login, plus the source-swap guard.
 *  §B  CTA-shown state — `ctaShown` on ResumeState (FUL-9 wiring). Pure merge +
 *      re-show rule so a returning visitor whose CTA was revealed does not need
 *      to re-watch to the threshold.
 *  §C  Return-prompt behavior — the pure `shouldOfferResume()` decision over
 *      (ResumeConfig, ResumeState, thresholds, duration): whether to prompt at
 *      all, and the "worth resuming" gate (not too early, not at the very end).
 *  §D  Local/cookie/server storage contract — `ResumeStorage` interface + the
 *      privacy-light client adapters (LocalStorage default, cookie fallback,
 *      in-memory mock) + the OPTIONAL server boundary.
 *  §E  Fixtures + mock — `FIXTURE_RESUME_STATE` + `MemoryResumeStorage` so
 *      FUL-9 CTA reveal and the player can wire against the contract with no
 *      browser and no credentials.
 *
 * Additive-only over contracts.ts: no existing type is narrowed or removed.
 * Privacy stance (PRD §9.6 + backend-engineer constraint "avoid collecting
 * unnecessary personal data"): the default MVP path is client-side LocalStorage
 * carrying no PII; server-side resume is OPTIONAL and powers cross-device resume
 * later, behind the same interface.
 *
 * Stable + additive; breaking changes require a contracts revision + CTO review.
 */

import type { ID, ISODate, ResumeConfig, ResumeState } from "./contracts";
import { DEFAULT_RESUME_THRESHOLDS } from "./contracts";
import type { ResumeMode } from "./contracts";

/* =====================================================================================
 * §A. Composite key derivation — last timestamp + max watched position anchor
 * ===================================================================================== */

/** Storage key prefix (mirrors the player's `fullvsl:visitor` convention). */
export const RESUME_STORAGE_KEY_PREFIX = "fullvsl:resume";

/** Maximum cookie value size before we fall back to memory (cookie ~4KB limit). */
export const RESUME_COOKIE_MAX_BYTES = 3500;

/**
 * Deterministic, dependency-free hash of a source URL. Used to (a) key resume
 * state and (b) detect media swaps under the same videoId (a swapped MP4 must
 * not resume from a stale position). FNV-1a 32-bit → base36; stable across
 * browsers and Node, no crypto import needed.
 */
export function sourceHash(sourceUrl: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < sourceUrl.length; i++) {
    h ^= sourceUrl.charCodeAt(i);
    // FNV prime (32-bit) with imul to keep within 32 bits.
    h = Math.imul(h, 0x01000193);
  }
  // Force unsigned 32-bit, then base36. Prefix avoids a leading-digit-only key.
  return `s${(h >>> 0).toString(36)}`;
}

/** Inputs to the composite resume key. */
export interface ResumeKeyInput {
  videoId: ID;
  /** Stable visitor identifier (from the player's localStorage visitorId). */
  visitorId: string;
  funnelId?: string;
  pageId?: string;
  /** Source hash from `sourceHash()`; guards against media swaps. */
  sourceHash: string;
}

/**
 * Deterministic composite resume key. Stable across visits for the same
 * visitor+video, so a returning visitor is recognized without login.
 *
 * Composite = `{videoId, visitorId, funnelId|pageId, sourceHash}`. Including
 * visitorId scopes the key per-browser (two visitors on the same machine do not
 * share resume state); funnelId/pageId further scope when a Full Funnel context
 * is present; sourceHash invalidates state when the underlying media changes.
 */
export function resumeKey(input: ResumeKeyInput): string {
  const scope = input.funnelId ?? input.pageId ?? "_";
  return [input.videoId, input.visitorId, scope, input.sourceHash].join(":");
}

/** Full localStorage / cookie key for a composite resume key. */
export function resumeStorageKey(key: string): string {
  return `${RESUME_STORAGE_KEY_PREFIX}:${key}`;
}

/**
 * Detect a media swap: true when the stored sourceHash differs from the current
 * one. On a swap the prior position is meaningless, so callers should clear the
 * stale state and start fresh.
 */
export function mediaSwapped(stored: ResumeState, currentSourceHash: string): boolean {
  return stored.sourceHash !== currentSourceHash;
}

/* =====================================================================================
 * §B. CTA-shown state (FUL-9 wiring)
 * ===================================================================================== */

/**
 * Merge a CTA-shown signal into resume state (monotonic). Once shown, it stays
 * shown until the state is cleared — a returning visitor re-sees the CTA without
 * re-watching to the reveal threshold. Returns a new state object (immutable).
 */
export function markCtaShown(state: ResumeState, shownAt: ISODate = new Date().toISOString()): ResumeState {
  if (state.ctaShown) return state;
  return { ...state, ctaShown: true, ctaShownAt: shownAt };
}

/**
 * Whether the player should skip the watched-progress reveal gate because the
 * CTA was already shown in a prior session. FUL-9 calls this on init; when true
 * it reveals the CTA immediately (no re-watch required).
 */
export function shouldReshowCta(state: ResumeState | null): boolean {
  return !!state?.ctaShown;
}

/* =====================================================================================
 * §C. Return-prompt behavior
 * ===================================================================================== */

export interface ResumePromptDecision {
  /** True when the resume prompt should be offered to the returning visitor. */
  offer: boolean;
  /** Position (seconds) to resume from when accepted. */
  resumeFromSec: number;
  /** Machine-readable reason (for analytics + tests). */
  reason:
    | "no_state" // nothing saved / first visit
    | "config_never" // ResumeConfig.mode === always_restart
    | "below_min_seconds" // not enough watched
    | "below_min_percent" // not enough progress
    | "near_end" // within tail — not worth resuming
    | "offer"; // prompt
}

/** Tail seconds from the end below which resuming is not useful. */
export const RESUME_TAIL_SEC = 5;

/**
 * Whether saved progress is "worth resuming": above the minimum watched seconds
 * AND minimum watched percent, and not within the tail of the video. Pure; the
 * player/server call this before deciding to prompt.
 */
export function worthResuming(
  state: ResumeState,
  thresholds: { minSeconds: number; minPercent: number } = DEFAULT_RESUME_THRESHOLDS,
  durationSec?: number,
): { worth: boolean; reason: ResumePromptDecision["reason"] } {
  if (state.maxWatchedSec < thresholds.minSeconds) {
    return { worth: false, reason: "below_min_seconds" };
  }
  const dur = durationSec ?? state.durationSec;
  if (dur && dur > 0) {
    const pct = (state.maxWatchedSec / dur) * 100;
    if (pct < thresholds.minPercent) {
      return { worth: false, reason: "below_min_percent" };
    }
    // Near the end → no value in resuming.
    if (dur - state.positionSec <= RESUME_TAIL_SEC) {
      return { worth: false, reason: "near_end" };
    }
  }
  return { worth: true, reason: "offer" };
}

/**
 * The return-prompt decision over (config, saved state, thresholds, duration).
 * Encodes the three ResumeMode behaviors:
 *  - `always_resume`: resume silently (no prompt) when there is saved state
 *    worth resuming; the player seeks directly. `offer=true` signals "resume"
 *    rather than "ask" — callers branch on `config.mode` to pick silent vs ask.
 *  - `always_restart`: never resume; start from 0 every visit.
 *  - `ask` (default): offer the resume prompt when worth resuming.
 *
 * Returns `{ offer: false, resumeFromSec: 0 }` for first visits / restart mode /
 * progress below threshold, so the caller has a single code path.
 */
export function shouldOfferResume(
  config: ResumeConfig,
  state: ResumeState | null,
  options?: {
    thresholds?: { minSeconds: number; minPercent: number };
    durationSec?: number;
  },
): ResumePromptDecision {
  if (!state) return { offer: false, resumeFromSec: 0, reason: "no_state" };
  if (config.mode === "always_restart") {
    return { offer: false, resumeFromSec: 0, reason: "config_never" };
  }
  const thresholds = options?.thresholds ?? DEFAULT_RESUME_THRESHOLDS;
  const { worth, reason } = worthResuming(state, thresholds, options?.durationSec ?? state.durationSec);
  if (!worth) return { offer: false, resumeFromSec: 0, reason };
  // For always_resume the player resumes silently; for "ask" it prompts. Both
  // report offer=true + the resume position so the caller can branch on mode.
  return { offer: true, resumeFromSec: state.positionSec, reason: "offer" };
}

/**
 * Whether, given the decision + mode, the player should show a visible prompt
 * (true) or resume silently (false). `always_resume` never shows a prompt.
 */
export function shouldShowResumePrompt(mode: ResumeMode, decision: ResumePromptDecision): boolean {
  return decision.offer && mode === "ask";
}

/* =====================================================================================
 * §D. Local / cookie / server storage contract
 *
 * The default MVP path is client-side LocalStorage (privacy-light, no PII).
 * `CookieResumeStorage` is the fallback when LocalStorage is unavailable
 * (private mode / disabled). `MemoryResumeStorage` is the no-DOM mock used by
 * tests, SSR, and the fixture path. The server-side `ResumeStore` (store.ts) is
 * an OPTIONAL cross-device path exposed via /api/v1/vsls/:id/resume.
 * ===================================================================================== */

export interface ResumeStorage {
  /** Load saved resume state for a composite key, or null when none/unreadable. */
  get(key: string): ResumeState | null;
  /** Persist a checkpoint (creates or updates). */
  put(state: ResumeState): void;
  /** Clear saved state for a key (media swap, completion, visitor request). */
  remove(key: string): void;
}

/**
 * Build a ResumeState checkpoint from live player values. Computes the key via
 * `resumeKey()` and stamps `updatedAt`. Use this on every progress/seek/pause
 * checkpoint so the persisted shape is consistent across adapters.
 */
export function createResumeCheckpoint(
  input: ResumeKeyInput,
  data: {
    positionSec: number;
    maxWatchedSec: number;
    durationSec?: number;
    ctaShown?: boolean;
    ctaShownAt?: ISODate;
  },
  updatedAt: ISODate = new Date().toISOString(),
): ResumeState {
  return {
    key: resumeKey(input),
    videoId: input.videoId,
    visitorId: input.visitorId,
    funnelId: input.funnelId,
    pageId: input.pageId,
    sourceHash: input.sourceHash,
    positionSec: data.positionSec,
    maxWatchedSec: data.maxWatchedSec,
    durationSec: data.durationSec,
    ctaShown: data.ctaShown,
    ctaShownAt: data.ctaShownAt,
    updatedAt,
  };
}

/** JSON (de)serialization shared by the local + cookie adapters. */
function serialize(state: ResumeState): string {
  return JSON.stringify(state);
}
function deserialize(raw: string | null | undefined): ResumeState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ResumeState;
    // Minimal shape guard — reject anything that is obviously not resume state.
    if (typeof parsed?.key === "string" && typeof parsed?.positionSec === "number") {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/** Resolve `globalThis.localStorage` without throwing in non-DOM runtimes. */
function safeLocalStorage(): Storage | null {
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
}

/**
 * LocalStorage-backed resume storage — the privacy-light default. Stores one
 * JSON blob per composite key under `fullvsl:resume:{key}`. No PII is persisted.
 * Throws a clear error if constructed in a runtime without localStorage so
 * callers fall back via `resolveResumeStorage()`.
 */
export class LocalStorageResumeStorage implements ResumeStorage {
  private readonly ls: Storage;
  constructor(localStorage?: Storage) {
    const ls = localStorage ?? safeLocalStorage();
    if (!ls) throw new Error("LocalStorageResumeStorage: localStorage unavailable");
    this.ls = ls;
  }
  get(key: string): ResumeState | null {
    return deserialize(this.ls.getItem(resumeStorageKey(key)));
  }
  put(state: ResumeState): void {
    this.ls.setItem(resumeStorageKey(state.key), serialize(state));
  }
  remove(key: string): void {
    this.ls.removeItem(resumeStorageKey(key));
  }
}

/**
 * Resolve `document.cookie` without throwing in non-DOM runtimes.
 */
function safeDocumentCookie(): string {
  try {
    return (globalThis as { document?: { cookie?: string } }).document?.cookie ?? "";
  } catch {
    return "";
  }
}
function setSafeDocumentCookie(value: string): boolean {
  try {
    const doc = (globalThis as { document?: { cookie: string } }).document;
    if (!doc) return false;
    doc.cookie = value;
    return true;
  } catch {
    return false;
  }
}

/**
 * Cookie-fallback resume storage for when LocalStorage is unavailable (private
 * mode / disabled). Cookie name = `fullvsl:resume:{key}`; value is URI-encoded
 * JSON. Respects the ~4KB cookie limit: writes that exceed it are dropped
 * (caller should fall back to in-session memory). No PII is persisted.
 */
export class CookieResumeStorage implements ResumeStorage {
  private readonly maxAgeDays: number;
  constructor(maxAgeDays = 90) {
    this.maxAgeDays = maxAgeDays;
  }
  private cookieName(key: string): string {
    return resumeStorageKey(key);
  }
  get(key: string): ResumeState | null {
    const name = this.cookieName(key);
    const match = safeDocumentCookie()
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`));
    if (!match) return null;
    const raw = decodeURIComponent(match.slice(name.length + 1));
    return deserialize(raw);
  }
  put(state: ResumeState): void {
    const name = this.cookieName(state.key);
    const raw = serialize(state);
    const encoded = encodeURIComponent(raw);
    if (encoded.length > RESUME_COOKIE_MAX_BYTES) return; // too big for a cookie
    const expires = new Date(Date.now() + this.maxAgeDays * 86400000).toUTCString();
    setSafeDocumentCookie(`${name}=${encoded}; expires=${expires}; path=/; SameSite=Lax`);
  }
  remove(key: string): void {
    const name = this.cookieName(key);
    setSafeDocumentCookie(`${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`);
  }
}

/**
 * Pick the best available client storage: LocalStorage → cookie → memory.
 * The player calls this once on init. `kind` reports which adapter won so the
 * player/UI can surface "resume unavailable" when only memory is available.
 */
export interface ResolvedResumeStorage {
  storage: ResumeStorage;
  kind: "local" | "cookie" | "memory";
}
export function resolveResumeStorage(): ResolvedResumeStorage {
  if (safeLocalStorage()) {
    try {
      return { storage: new LocalStorageResumeStorage(), kind: "local" };
    } catch {
      /* fall through */
    }
  }
  if ((globalThis as { document?: unknown }).document) {
    return { storage: new CookieResumeStorage(), kind: "cookie" };
  }
  return { storage: new MemoryResumeStorage(), kind: "memory" };
}

/* =====================================================================================
 * §E. Fixtures + mock
 * ===================================================================================== */

/** Fixture composite-key inputs (matches the demo/sample MP4). */
export const FIXTURE_RESUME_INPUT: ResumeKeyInput = {
  videoId: "vsl-fixture",
  visitorId: "visitor-fixture",
  funnelId: "fixture-location",
  sourceHash: sourceHash("https://assets.cdn.filesafe.space/xqBFeOe8aUuZI8tfnSDv/media/67998dec4f0aeba88060946c.mp4"),
};
export const FIXTURE_RESUME_KEY = resumeKey(FIXTURE_RESUME_INPUT);

/**
 * Fixture resume state: a returning visitor who watched ~30s of a ~120s video
 * and whose CTA was revealed. The shape FUL-9 / the player load on init.
 */
export const FIXTURE_RESUME_STATE: ResumeState = {
  key: FIXTURE_RESUME_KEY,
  videoId: FIXTURE_RESUME_INPUT.videoId,
  visitorId: FIXTURE_RESUME_INPUT.visitorId,
  funnelId: FIXTURE_RESUME_INPUT.funnelId,
  sourceHash: FIXTURE_RESUME_INPUT.sourceHash,
  positionSec: 30,
  maxWatchedSec: 30,
  durationSec: 120,
  ctaShown: true,
  ctaShownAt: "2026-07-02T08:01:00.000Z",
  updatedAt: "2026-07-02T08:01:30.000Z",
};

/**
 * In-memory resume storage — the no-DOM mock for tests, SSR, and the fixture
 * path. Drop-in for `LocalStorageResumeStorage` / `CookieResumeStorage`.
 */
export class MemoryResumeStorage implements ResumeStorage {
  private readonly byKey = new Map<string, ResumeState>();
  constructor(seed: ResumeState[] = []) {
    for (const s of seed) this.byKey.set(s.key, { ...s });
  }
  get(key: string): ResumeState | null {
    const s = this.byKey.get(key);
    return s ? { ...s } : null;
  }
  put(state: ResumeState): void {
    this.byKey.set(state.key, { ...state });
  }
  remove(key: string): void {
    this.byKey.delete(key);
  }
  /** Test helper: all seeded/stored states. */
  snapshot(): ResumeState[] {
    return [...this.byKey.values()].map((s) => ({ ...s }));
  }
}
